import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import {
  Bot, CheckCircle2, XCircle, User, Trash2,
  Bell, Plus, MessageCircle, ShoppingCart, HelpCircle, Sparkles, Link2, Copy, RefreshCw, Pencil, Save, X, QrCode, Download,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import InsightsAISection from "./InsightsAISection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESEMPI_DOMANDE = [
  { categoria: "Incassi & Cassa", domande: [
    "Quanto abbiamo incassato ieri?",
    "Qual e il totale incassi di questa settimana?",
    "Confronta gli incassi di gennaio e febbraio",
    "Quanto c'e nel fondo cassa?",
  ]},
  { categoria: "Spese & Fornitori", domande: [
    "Quanto abbiamo speso in totale questo mese?",
    "Quanto ci costa la mozzarella?",
    "Da quali fornitori acquistiamo il pomodoro?",
  ]},
  { categoria: "Personale & Turni", domande: [
    "Chi lavora oggi?",
    "Quante ore ha fatto Mario questo mese?",
    "Quanti giorni di ferie restano a Giulia?",
  ]},
  { categoria: "Magazzino & Lotti", domande: [
    "Ci sono lotti in scadenza?",
    "Mostrami le giacenze di magazzino",
    "Qual e lo storico prezzi della farina?",
  ]},
  { categoria: "Ricette & Menu", domande: [
    "Qual e il costo della pizza margherita?",
    "Qual e il food cost della carbonara?",
    "Quali sono gli ingredienti piu usati?",
  ]},
  { categoria: "Inserimento Dati", domande: [
    "Inserisci incasso: contanti 500, carte 300, buoni 50",
    "Registra una spesa di 200 euro per la mozzarella dal fornitore Metro",
  ]},
];

const IntegrazioneTelegram = () => {
  const { getAuthHeaders, user } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [centralInfo, setCentralInfo] = useState({ bot_username: "", bot_configured: false, slots: [] });
  const [logs, setLogs] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [creatingSlot, setCreatingSlot] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editName, setEditName] = useState("");
  const [requestingSlot, setRequestingSlot] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [qrSlot, setQrSlot] = useState(null);
  const [addonPreview, setAddonPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [stripeQuote, setStripeQuote] = useState(null);
  const [upgradePending, setUpgradePending] = useState(false);
  const upgradeKey = `yollo-telegram-upgrade:${user?.societa_id || "unselected"}`;

  const authHdrs = useCallback(() => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadAll = useCallback(async () => {
    const hdrs = authHdrs();
    try {
      const [pricingR, centralR, logsR, pendingR] = await Promise.all([
        fetch(`${API}/billing/pricing-summary`, { headers: hdrs }),
        fetch(`${API}/telegram/central/info`, { headers: hdrs }),
        fetch(`${API}/telegram/logs?limit=30`, { headers: hdrs }),
        fetch(`${API}/billing/my-requests`, { headers: hdrs }),
      ]);
      if (pricingR.ok) setPricing(await pricingR.json());
      if (centralR.ok) setCentralInfo(await centralR.json());
      if (logsR.ok) setLogs(await logsR.json());
      if (pendingR.ok) setPendingRequests(await pendingR.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authHdrs]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const slots = centralInfo.slots || [];
  const tgPkg = pricing?.telegram_bot;
  const hasBot = tgPkg?.attivo || slots.length > 0;
  const numeriAcquistati = Math.max(tgPkg?.numeri_acquistati || 0, slots.length);
  const slotConnessi = slots.filter(s => s.connected).length;
  const prezzoSlot = tgPkg?.prezzo_numero_aggiuntivo || 5;
  const hasPendingBot = pendingRequests.some(r => r.tipo === "bot_telegram" && r.stato === "pending");

  const handleCreateSlot = async () => {
    if (slots.length >= numeriAcquistati) {
      toast.error("Limite slot raggiunto. Acquista numeri aggiuntivi.");
      return;
    }
    setCreatingSlot(true);
    try {
      const res = await fetch(`${API}/telegram/slots`, { method: "POST", headers: authHdrs() });
      if (res.ok) {
        toast.success("Nuovo slot creato. Condividi il link con la persona.");
        loadAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore creazione slot");
      }
    } catch {
      toast.error("Errore di connessione");
    } finally {
      setCreatingSlot(false);
    }
  };

  const handleRegenerate = async (idx) => {
    if (!await confirm("Rigenerare il link? Il chat collegato verrà scollegato.", { confirmLabel: "Rigenera" })) return;
    try {
      const res = await fetch(`${API}/telegram/slots/${idx}/regenerate`, { method: "POST", headers: authHdrs() });
      if (res.ok) {
        toast.success("Link rigenerato");
        loadAll();
      } else {
        toast.error("Errore");
      }
    } catch {
      toast.error("Errore");
    }
  };

  const handleRemove = async (idx) => {
    if (!await confirm("Eliminare definitivamente questo slot?", { confirmLabel: "Elimina" })) return;
    try {
      const res = await fetch(`${API}/telegram/destinatari/by-index/${idx}`, { method: "DELETE", headers: authHdrs() });
      if (res.ok) {
        toast.success("Slot rimosso");
        loadAll();
      } else {
        toast.error("Errore");
      }
    } catch {
      toast.error("Errore");
    }
  };

  const handleRename = async (idx) => {
    const nome = (editName || "").trim();
    if (!nome) { toast.error("Nome obbligatorio"); return; }
    try {
      const res = await fetch(`${API}/telegram/slots/${idx}/rename?nome=${encodeURIComponent(nome)}`, {
        method: "PUT", headers: authHdrs(),
      });
      if (res.ok) {
        toast.success("Slot rinominato");
        setEditingIdx(null);
        loadAll();
      }
    } catch {
      toast.error("Errore");
    }
  };

  const handleToggle = async (telegramId, attivo) => {
    if (!telegramId) return;
    try {
      await fetch(`${API}/telegram/destinatari/${telegramId}/toggle?attivo=${!attivo}`, {
        method: "PUT", headers: authHdrs(),
      });
      loadAll();
    } catch { /* noop */ }
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById("telegram-qr-canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `telegram-qr-${(qrSlot?.nome || "slot").replace(/\s+/g, "_")}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("QR scaricato");
  };

  const fetchPreview = async (code) => {
    setLoadingPreview(true);
    try {
      const q = `quantita=1${code ? `&codice_sconto=${encodeURIComponent(code)}` : ""}`;
      const res = await fetch(`${API}/billing/addon-telegram-preview?${q}`, { headers: authHdrs() });
      if (res.ok) {
        setAddonPreview(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel calcolo dell'anteprima");
        if (!code) setPreviewOpen(false);
      }
    } catch {
      toast.error("Errore di connessione");
      if (!code) setPreviewOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const openAddonPreview = async () => {
    if (upgradePending) {
      toast.error("Verifica prima il pagamento precedente");
      return;
    }
    setPreviewOpen(true);
    setStripeQuote(null);
    setAddonPreview(null);
    setDiscountCode("");
    await fetchPreview("");
  };

  const applyDiscount = async () => {
    const code = (discountCode || "").trim();
    if (!code) return;
    setApplyingDiscount(true);
    await fetchPreview(code);
    setApplyingDiscount(false);
  };

  const handleRequestAddon = async () => {
    if (upgradePending) return;
    setRequestingSlot(true);
    try {
      const validCode = addonPreview?.sconto ? (discountCode || "").trim() : "";
      const fingerprint = JSON.stringify(["telegram_add_numeri", 1, validCode.toUpperCase()]);
      const old = JSON.parse(sessionStorage.getItem(upgradeKey) || "null");
      if (old && old.fingerprint !== fingerprint) {
        toast.error("La selezione è cambiata: chiudi il preventivo e riprova");
        return;
      }
      const request = old || { id: crypto.randomUUID(), fingerprint };
      sessionStorage.setItem(upgradeKey, JSON.stringify(request));
      const res = await fetch(`${API}/billing/purchase-addon`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHdrs() },
        body: JSON.stringify({ tipo: "telegram_add_numeri", quantita: 1, codice_sconto: validCode,
          upgrade_request_id: request.id, preview_only: !stripeQuote }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.mode === "stripe_upgrade_quote") {
          setStripeQuote(data);
        } else if (data.mode === "stripe_upgrade_pending") {
          setStripeQuote(null);
          setUpgradePending(true);
          toast.info("Pagamento in verifica: nessun nuovo numero ancora attivato");
        } else {
          sessionStorage.removeItem(upgradeKey);
          setStripeQuote(null);
          toast.success("Pagamento confermato: nuovo numero disponibile");
          setPreviewOpen(false);
          loadAll();
        }
      } else {
        const err = await res.json().catch(() => ({}));
        if ([400, 402, 404, 422].includes(res.status)) sessionStorage.removeItem(upgradeKey);
        else setUpgradePending(true);
        setStripeQuote(null);
        toast.error(err.detail || "Errore nell'acquisto");
      }
    } catch {
      setStripeQuote(null);
      setUpgradePending(true);
      toast.error("Pagamento in verifica: controlla lo stato prima di riprovare");
    } finally {
      setRequestingSlot(false);
    }
  };

  const checkTelegramUpgrade = useCallback(async () => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(upgradeKey) || "null");
      if (!stored?.id) { setUpgradePending(false); return; }
      const res = await fetch(`${API}/billing/upgrade-status/${stored.id}`, { headers: authHdrs() });
      if (res.status === 404) { sessionStorage.removeItem(upgradeKey); setUpgradePending(false); return; }
      if (!res.ok) return;
      const data = await res.json();
      if (["completed", "failed", "quoted"].includes(data.status)) {
        sessionStorage.removeItem(upgradeKey);
        setUpgradePending(false);
        if (data.status === "completed") { toast.success("Numero acquistato e pagamento confermato"); loadAll(); }
      } else setUpgradePending(true);
    } catch { setUpgradePending(true); }
  }, [upgradeKey, authHdrs, loadAll]);

  useEffect(() => { if (user?.societa_id && sessionStorage.getItem(upgradeKey)) checkTelegramUpgrade(); }, [user?.societa_id, upgradeKey, checkTelegramUpgrade]);

  const closeAddonPreview = () => {
    if (requestingSlot) return;
    if (stripeQuote) sessionStorage.removeItem(upgradeKey);
    setStripeQuote(null);
    setPreviewOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="telegram-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Bot Telegram</h1>
          <p className="text-sm text-gray-400 mt-1">
            {centralInfo.bot_username
              ? <>Connessione centralizzata tramite <span className="font-mono text-blue-600">@{centralInfo.bot_username}</span></>
              : "Configurazione bot in corso..."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasBot ? (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1.5 text-xs font-bold py-1 px-3" data-testid="bot-status-active">
              <CheckCircle2 className="h-3.5 w-3.5" /> {slotConnessi}/{slots.length} connessi
            </Badge>
          ) : hasPendingBot ? (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 gap-1.5 text-xs font-bold py-1 px-3" data-testid="bot-status-pending">
              <HelpCircle className="h-3.5 w-3.5" /> In attesa
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-gray-100 text-gray-500 gap-1.5 text-xs font-bold py-1 px-3" data-testid="bot-status-inactive">
              <XCircle className="h-3.5 w-3.5" /> Non attivo
            </Badge>
          )}
        </div>
      </div>

      {/* Pending request banner */}
      {hasPendingBot && !hasBot && (
        <div className="rounded-2xl border-2 border-dashed border-amber-200 bg-amber-50/40 p-6 text-center" data-testid="pending-bot-banner">
          <HelpCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-800 mb-1">Richiesta in lavorazione</h3>
          <p className="text-sm text-gray-500">
            La tua richiesta per il Bot Telegram è stata inoltrata. Entro 48 ore riceverai l&apos;attivazione.
          </p>
        </div>
      )}

      {/* No bot purchased */}
      {!hasBot && !hasPendingBot && (
        <div className="rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/40 p-6 text-center" data-testid="no-bot-banner">
          <Bot className="h-10 w-10 text-blue-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-800 mb-1">Bot Telegram non attivo</h3>
          <p className="text-sm text-gray-500 mb-4">
            Attiva il pacchetto Bot Telegram per ricevere notifiche e interrogare il gestionale.</p>
          <Button onClick={() => window.location.href = "/abbonamento"}
            className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold"
            data-testid="go-to-abbonamento-btn">
            <ShoppingCart className="h-4 w-4 mr-1.5" /> Vai all&apos;Abbonamento
          </Button>
        </div>
      )}

      {/* SLOT TELEGRAM */}
      {hasBot && (
        <div className="rounded-2xl border border-gray-200/60 bg-white shadow-sm overflow-hidden" data-testid="slots-section">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                  <Bell className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Connessioni Telegram</h2>
                  <p className="text-xs text-gray-400">{slots.length}/{numeriAcquistati} slot — {slotConnessi} connessi</p>
                </div>
              </div>
              <div className="w-24 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full bg-blue-500 transition-all"
                  style={{ width: `${numeriAcquistati > 0 ? Math.min(100, (slotConnessi / numeriAcquistati) * 100) : 0}%` }} />
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Help banner: come funziona */}
            <div className="rounded-xl bg-blue-50/60 border border-blue-100 p-3">
              <p className="text-xs text-blue-900 leading-relaxed">
                <b>Come funziona:</b> per ogni slot generi un link univoco. Condividilo con la persona via WhatsApp/Email.
                Quando preme START sul link, viene collegata automaticamente. Non serve più configurare un bot separato.
              </p>
            </div>

            {slots.length === 0 ? (
              <div className="text-center py-6">
                <Bot className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Nessuno slot configurato</p>
              </div>
            ) : (
              <div className="space-y-2">
                {slots.map((s) => (
                  <div key={s.idx} className="rounded-xl border border-gray-100 bg-gray-50/40 overflow-hidden" data-testid={`slot-${s.idx}`}>
                    {/* Header: nome + stato */}
                    <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-white">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.connected ? "bg-emerald-100" : "bg-amber-100"}`}>
                          {s.connected ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Link2 className="h-4 w-4 text-amber-600" />}
                        </div>
                        {editingIdx === s.idx ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input value={editName} onChange={e => setEditName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleRename(s.idx); if (e.key === 'Escape') setEditingIdx(null); }}
                              placeholder="Nome slot" className="h-8 rounded-lg text-sm" data-testid={`edit-name-${s.idx}`} />
                            <button onClick={() => handleRename(s.idx)}
                              className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50" data-testid={`save-rename-${s.idx}`}>
                              <Save className="h-4 w-4" />
                            </button>
                            <button onClick={() => setEditingIdx(null)}
                              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100" data-testid={`cancel-rename-${s.idx}`}>
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{s.nome}</p>
                            <p className="text-xs text-gray-400">
                              {s.connected ? (
                                <>Connesso: {s.telegram_first_name || s.telegram_username || s.telegram_id}</>
                              ) : (
                                "In attesa di connessione"
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {s.connected && (
                          <button onClick={() => handleToggle(s.telegram_id, s.attivo)}
                            className={`px-2 py-1 rounded-lg text-xs font-semibold transition-all ${
                              s.attivo ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-gray-200 text-gray-500 hover:bg-gray-300"
                            }`}
                            data-testid={`toggle-${s.idx}`}>
                            {s.attivo ? "Attivo" : "Disattivato"}
                          </button>
                        )}
                        {editingIdx !== s.idx && (
                          <button onClick={() => { setEditingIdx(s.idx); setEditName(s.nome); }}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                            data-testid={`rename-${s.idx}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={() => handleRegenerate(s.idx)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50"
                          title="Rigenera link"
                          data-testid={`regen-${s.idx}`}>
                          <RefreshCw className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleRemove(s.idx)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`remove-${s.idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Link area (solo se non connesso) */}
                    {!s.connected && s.connection_link && (
                      <div className="p-3 bg-amber-50/40">
                        <p className="text-[10px] uppercase tracking-wide font-bold text-amber-700 mb-1.5">Link di connessione</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-2 py-1.5 rounded-lg bg-white border border-amber-200 text-xs text-gray-700 font-mono truncate"
                            data-testid={`link-${s.idx}`}>
                            {s.connection_link}
                          </code>
                          <Button size="sm" onClick={() => handleCopyLink(s.connection_link)}
                            className="h-8 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs gap-1 px-3"
                            data-testid={`copy-${s.idx}`}>
                            <Copy className="h-3 w-3" /> Copia
                          </Button>
                          <Button size="sm" onClick={() => setQrSlot(s)}
                            className="h-8 rounded-lg bg-gray-800 hover:bg-gray-900 text-white text-xs gap-1 px-3"
                            data-testid={`qr-${s.idx}`}>
                            <QrCode className="h-3 w-3" /> QR
                          </Button>
                        </div>
                        <p className="text-[10px] text-amber-700 mt-1.5">
                          Condividi questo link via WhatsApp/Email. Una volta usato, il chat verrà collegato a questo slot.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Crea nuovo slot */}
            {slots.length < numeriAcquistati && (
              <Button onClick={handleCreateSlot} disabled={creatingSlot}
                className="w-full h-10 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold gap-2"
                data-testid="create-slot-btn">
                {creatingSlot ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Plus className="h-4 w-4" />}
                Crea nuovo slot di connessione
              </Button>
            )}

            {slots.length >= numeriAcquistati && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-blue-50/60 border border-blue-100">
                  <div>
                    <p className="text-sm font-bold text-gray-800">Hai bisogno di piu numeri?</p>
                    <p className="text-xs text-gray-500 mt-0.5">Aggiungi un nuovo slot a +{prezzoSlot}€/mese</p>
                  </div>
                  <Button onClick={openAddonPreview} disabled={requestingSlot}
                    className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold px-4" data-testid="request-slot-btn">
                    {requestingSlot ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <><Plus className="h-3.5 w-3.5 mr-1" /> Acquista slot</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* INSIGHTS AI */}
      {hasBot && <InsightsAISection getAuthHeaders={getAuthHeaders} />}

      {/* ESEMPI DOMANDE */}
      <div className="rounded-2xl border border-gray-200/60 bg-white shadow-sm overflow-hidden" data-testid="esempi-section">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <HelpCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Cosa puoi chiedere al Bot</h2>
              <p className="text-xs text-gray-400">Esempi di domande</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-2">
          {ESEMPI_DOMANDE.map((cat, i) => (
            <div key={i} className="rounded-xl border border-gray-100 overflow-hidden">
              <button onClick={() => setExpandedCat(expandedCat === i ? null : i)}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 text-left" data-testid={`cat-${i}`}>
                <span className="text-sm font-semibold text-gray-700">{cat.categoria}</span>
                <span className="text-xs text-gray-400">{cat.domande.length} esempi</span>
              </button>
              {expandedCat === i && (
                <div className="px-3 pb-3 space-y-1.5">
                  {cat.domande.map((d, j) => (
                    <div key={j} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50">
                      <MessageCircle className="h-3.5 w-3.5 text-violet-400 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-gray-600">{d}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ULTIME CONVERSAZIONI */}
      <div className="rounded-2xl border border-gray-200/60 bg-white shadow-sm overflow-hidden" data-testid="conversazioni-section">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Ultime Conversazioni</h2>
              <p className="text-xs text-gray-400">Le ultime interazioni con il bot</p>
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          {logs.length > 0 ? (
            <ScrollArea className="max-h-[400px]">
              {logs.map((log, i) => (
                <div key={i} className="px-5 py-3 hover:bg-gray-50/50" data-testid={`log-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="h-3.5 w-3.5 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-gray-700">{log.user_name || "Utente"}</span>
                        <span className="text-[10px] text-gray-300">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mb-1.5 line-clamp-2">{log.message}</p>
                      {log.response && (
                        <div className="flex items-start gap-2 p-2 rounded-lg bg-emerald-50/50">
                          <Bot className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-emerald-700 line-clamp-3">{log.response}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </ScrollArea>
          ) : (
            <div className="px-5 py-10 text-center">
              <Sparkles className="h-8 w-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Nessuna conversazione registrata</p>
            </div>
          )}
        </div>
      </div>

      {/* QR DIALOG */}
      <Dialog open={!!qrSlot} onOpenChange={(o) => !o && setQrSlot(null)}>
        <DialogContent className="max-w-md" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-blue-500" />
              Connessione rapida — {qrSlot?.nome}
            </DialogTitle>
            <DialogDescription>
              Mostra questo QR al telefono della persona da collegare. Aprirà Telegram e premerà START automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {qrSlot?.connection_link ? (
              <>
                <div className="p-4 rounded-2xl bg-white border-2 border-gray-100 shadow-sm">
                  <QRCodeCanvas
                    id="telegram-qr-canvas"
                    value={qrSlot.connection_link}
                    size={260}
                    level="M"
                    includeMargin={false}
                    imageSettings={{
                      src: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0OCA0OCI+PGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiMyOUE5RUEiLz48cGF0aCBkPSJNMTMgMjMuOWw4LjMgMy4xIDMuMSA5LjMgMTUuMS0xOC4yeiIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==",
                      height: 36,
                      width: 36,
                      excavate: true,
                    }}
                  />
                </div>
                <div className="w-full">
                  <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400 mb-1.5 text-center">Link diretto</p>
                  <code className="block w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-700 font-mono break-all text-center">
                    {qrSlot.connection_link}
                  </code>
                </div>
                <div className="flex items-center gap-2 w-full">
                  <Button onClick={() => handleCopyLink(qrSlot.connection_link)}
                    className="flex-1 h-10 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold gap-2"
                    data-testid="qr-copy-btn">
                    <Copy className="h-4 w-4" /> Copia link
                  </Button>
                  <Button onClick={handleDownloadQR}
                    className="flex-1 h-10 rounded-xl bg-gray-800 hover:bg-gray-900 text-white font-semibold gap-2"
                    data-testid="qr-download-btn">
                    <Download className="h-4 w-4" /> Scarica QR
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400">Nessun link disponibile.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog anteprima addebito proporzionale "aggiungi numero" */}
      <Dialog open={previewOpen} onOpenChange={(o) => !o && closeAddonPreview()}>
        <DialogContent className="max-w-sm" data-testid="addon-preview-dialog">
          <DialogHeader>
            <DialogTitle className="mr-10 flex items-start gap-2 text-base leading-snug" data-testid="telegram-upgrade-dialog-title">
              <Plus className="h-5 w-5 shrink-0 text-blue-500" />
              <span className="min-w-0">Aggiungi un numero Bot Telegram</span>
            </DialogTitle>
            <DialogDescription>
              La stima viene confermata da Stripe prima dell'acquisto.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {loadingPreview || !addonPreview ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-3">
                {addonPreview.is_trial ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="addon-preview-trial">
                    <p className="text-sm font-bold text-amber-700">Sei in periodo di prova</p>
                    {stripeQuote ? (
                      <p className="text-sm text-amber-700 mt-1" data-testid="telegram-stripe-quote-amount">
                        Nessun addebito oggi: il nuovo numero sarà fatturato al termine della prova.
                      </p>
                    ) : <p className="text-xs text-amber-600 mt-1">
                      Nessun addebito oggi. Il numero verrà conteggiato (
                      {addonPreview.sconto && addonPreview.importo_mensile_scontato < addonPreview.importo_mensile ? (
                        <>
                          <span className="line-through mr-1">+{addonPreview.importo_mensile.toFixed(2)}€</span>
                          +{addonPreview.importo_mensile_scontato.toFixed(2)}€/mese
                        </>
                      ) : (
                        <>+{addonPreview.importo_mensile.toFixed(2)}€/mese</>
                      )}
                      ) a partire dal primo rinnovo{addonPreview.data_rinnovo ? ` del ${addonPreview.data_rinnovo}` : ""}.
                    </p>}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between p-4 rounded-xl bg-blue-50 border border-blue-100">
                      <div>
                        <p className="text-xs uppercase tracking-wide font-bold text-blue-400">{stripeQuote ? "Importo Stripe oggi" : "Stima oggi"}</p>
                        <p className="text-[11px] text-blue-500 mt-0.5">Quota proporzionale fino al rinnovo</p>
                      </div>
                      <div className="text-right">
                        {!stripeQuote && addonPreview.sconto && (addonPreview.importo_oggi_scontato ?? addonPreview.importo_oggi) < addonPreview.importo_oggi && (
                          <p className="text-sm text-gray-400 line-through">{addonPreview.importo_oggi.toFixed(2)}€</p>
                        )}
                        <p className="text-2xl font-extrabold text-blue-600" data-testid="addon-preview-oggi">
                          {stripeQuote ? <span data-testid="telegram-stripe-quote-amount">{Number(stripeQuote.amount_today).toFixed(2)}€</span>
                            : `${(addonPreview.sconto ? (addonPreview.importo_oggi_scontato ?? addonPreview.importo_oggi) : addonPreview.importo_oggi).toFixed(2)}€`}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-gray-500 px-1">
                      <span>{stripeQuote ? "Al prossimo rinnovo" : `Poi dal ${addonPreview.data_rinnovo || "prossimo rinnovo"}`}</span>
                      <span className="ml-auto text-right font-semibold text-gray-700">
                        {stripeQuote ? (
                          <span data-testid="telegram-stripe-quote-renewal">{stripeQuote.duration === "ricorrente" ? "Sconto ricorrente sul nuovo numero" : "Prezzo ordinario del nuovo numero"}</span>
                        ) : addonPreview.sconto && addonPreview.importo_mensile_scontato < addonPreview.importo_mensile ? (
                          <>
                            <span className="line-through text-gray-400 mr-1">+{addonPreview.importo_mensile.toFixed(2)}€</span>
                            +{addonPreview.importo_mensile_scontato.toFixed(2)}€/mese
                          </>
                        ) : (
                          <>+{addonPreview.importo_mensile.toFixed(2)}€/mese</>
                        )}
                      </span>
                    </div>
                  </>
                )}

                {/* Codice sconto */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex gap-2">
                    <Input value={discountCode} disabled={Boolean(stripeQuote)}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyDiscount(); } }}
                      placeholder="Codice sconto (facoltativo)"
                      className="h-10 rounded-xl text-sm" data-testid="addon-discount-input" />
                    <Button type="button" variant="outline" onClick={applyDiscount}
                      disabled={applyingDiscount || Boolean(stripeQuote) || !discountCode.trim()}
                      className="h-10 rounded-xl text-sm px-4 shrink-0" data-testid="addon-discount-apply-btn">
                      {applyingDiscount ? "..." : "Applica"}
                    </Button>
                  </div>
                  {addonPreview.sconto && (
                    <p className="text-xs text-emerald-600 font-semibold" data-testid="addon-discount-ok">
                      Codice {addonPreview.sconto.codice} {stripeQuote ? "verificato su Stripe per il nuovo numero" : "applicato"} — {addonPreview.sconto.tipo_sconto === "percentuale" ? `-${addonPreview.sconto.valore}%` : `-${addonPreview.sconto.valore}€`}{addonPreview.sconto.durata_sconto === "primo_mese" ? " (solo 1ª quota)" : ""}
                    </p>
                  )}
                  {addonPreview.sconto_errore && (
                    <p className="text-xs text-red-500" data-testid="addon-discount-error">{addonPreview.sconto_errore}</p>
                  )}
                </div>

                {upgradePending && <Button variant="outline" onClick={checkTelegramUpgrade} data-testid="telegram-upgrade-check-status">Verifica pagamento</Button>}
                <Button onClick={handleRequestAddon} disabled={requestingSlot || upgradePending}
                  className="w-full h-11 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-bold gap-2"
                  data-testid="addon-preview-confirm-btn">
                  {requestingSlot ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>{stripeQuote ? "Conferma upgrade" : "Verifica importo su Stripe"}</>
                  )}
                </Button>
                <button type="button" onClick={closeAddonPreview}
                  className="w-full text-xs text-gray-400 hover:text-gray-600 py-1" data-testid="addon-preview-cancel-btn">
                  Annulla
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrazioneTelegram;
